import React, {useContext, useEffect} from 'react';

import {useActiveThread} from '../../agentChat/store/context';
import {useDismissibleNotice} from '../../agentChat/useDismissibleNotice';
import {trackNavRightFeatureOpened} from '../../services/analytics/productEvents';
import {
  useIsAdminModeActiveAndIsNotLocalEnv,
  viewingAs,
} from '../../util/admin';
import {AgentChatProvider, useAgentChatAvailability} from '../AgentChat';
import {AgentChatRuntimeBoundary} from '../AgentChat/AgentChatProvider';
import {AccountSelectorContext} from '../Search/SearchNav/AccountSelectorContextProvider';
import {ThreadHistoryProvider} from './chatHistory/ThreadHistoryProvider';
import {useTrackWBAgentPanel} from './useTrackWBAgent';
import {WBAgentChatButton} from './WBAgentChatButton';
import {WBAgentChatDrawer} from './WBAgentChatDrawer';
import {WBAgentChatWindow} from './WBAgentChatWindow';
import {useWBAgentContextStore} from './WBAgentContextStore';
import {WBAgentRunDisplayNameProvider} from './WBAgentRunDisplayNameProvider';
import {WBAgentSessionRestore} from './WBAgentSessionRestore';

/**
 * Component tree:
 * WBAgent → WBAgentInner
 *   ├── WBAgentChatButton
 *   ├── WBAgentChatWindow (floating, undocked)
 *   │   ├── WBAgentHeader
 *   │   └── WBAgentThread
 *   │       ├── WBAgentMessageAssistant
 *   │       ├── WBAgentMessageUser
 *   │       └── WBAgentComposer
 *   │           └── WBAgentEditor
 *   └── WBAgentChatDrawer (docked)
 *       ├── WBAgentHeader
 *       └── WBAgentThread
 *           └── ...
 */

export const WBAgent = () => {
  return (
    <AgentChatProvider>
      <WBAgentInner />
    </AgentChatProvider>
  );
};

export const WBAgentInner = () => {
  const {isAvailable: isWorkspaceAgentEnabled} = useAgentChatAvailability();
  const activeThread = useActiveThread();
  const open = useWBAgentContextStore(s => s.open);
  const setOpen = useWBAgentContextStore(s => s.setOpen);
  const docked = useWBAgentContextStore(s => s.docked);
  const {selectedAccount} = useContext(AccountSelectorContext);
  const isAdminMode =
    useIsAdminModeActiveAndIsNotLocalEnv() || viewingAs() !== '';
  const {dismissed: introDismissed, dismiss: dismissIntro} =
    useDismissibleNotice('wbagent-intro');
  const {trackOpened, trackClosed, trackNavbarButtonViewed} =
    useTrackWBAgentPanel();

  // If the chat is already open on mount, the user has discovered it — skip the cue.
  useEffect(() => {
    if (open) {
      dismissIntro();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {isWorkspaceAgentEnabled && (
        <WBAgentChatButton
          showOnboardingCue={!introDismissed}
          onImpression={trackNavbarButtonViewed}
          onPress={() => {
            if (!open) {
              trackNavRightFeatureOpened({
                feature: 'agent',
                selected_account: selectedAccount,
                is_admin_mode: isAdminMode,
              });
              trackOpened();
            } else {
              trackClosed();
            }
            if (!introDismissed) {
              dismissIntro();
            }
            setOpen(!open);
          }}
          isLoading={activeThread?.status === 'running'}
        />
      )}
      <AgentChatRuntimeBoundary>
        <WBAgentSessionRestore />
        {open && (
          <WBAgentRunDisplayNameProvider>
            <ThreadHistoryProvider>
              {docked ? <WBAgentChatDrawer /> : <WBAgentChatWindow />}
            </ThreadHistoryProvider>
          </WBAgentRunDisplayNameProvider>
        )}
      </AgentChatRuntimeBoundary>
    </>
  );
};
